/**
 * Native camera bridge for Expo WebView
 *
 * This utility enables the webapp to use the native camera when running inside
 * the Expo mobile app. It sends a TakePhoto/TakeVideo message to the native app
 * which opens expo-image-picker, and returns the captured media as base64.
 */

import { mobileEventBus$ } from 'AppRoot'
import { Message, MsgSource, MsgTypes, sendAndAwaitReply } from '../messaging/messaging'

declare global {
    interface Window {
        KNOVIGATOR_IS_MOBILE?: boolean
        ReactNativeWebView?: {
            postMessage: (message: string) => void
        }
    }
}

export interface PhotoResult {
    uri: string
    base64: string
    width: number
    height: number
    type: string
    fileName: string
}

export interface VideoResult {
    uri: string
    base64: string
    width: number
    height: number
    duration: number
    type: string
    fileName: string
}

interface MediaResultResponse extends PhotoResult {
    duration?: number
    cancelled?: boolean
    error?: string
}

interface CameraPermissionResponse {
    granted?: boolean
    canAskAgain?: boolean
    error?: string
}

/**
 * Check if the webapp is running inside the Expo WebView
 */
export function isRunningInExpoWebView(): boolean {
    return !!window.KNOVIGATOR_IS_MOBILE && !!window.ReactNativeWebView
}

export function hasNativeCameraPermissionGate(): boolean {
    return isRunningInExpoWebView() && window.KNOVIGATOR_NATIVE_CAPS?.cameraPermissionGate === true
}

export function hasNativeVideoCaptureInput(): boolean {
    return isRunningInExpoWebView() && window.KNOVIGATOR_NATIVE_CAPS?.videoCaptureInput === true
}

export async function prepareCameraInput(): Promise<boolean> {
    if (!hasNativeCameraPermissionGate()) return true

    const message = new Message('', MsgSource.Web, MsgTypes.PrepareCamera)
    const reply = await sendAndAwaitReply(mobileEventBus$, message, {
        timeout: 60_000,
        retries: 0,
    })
    const result = JSON.parse(reply.content || '{}') as CameraPermissionResponse
    if (result.error) throw new Error(result.error)
    return result.granted === true
}

/**
 * Take a photo using the native camera
 *
 * @returns PhotoResult with base64 data, or null if cancelled
 * @throws Error if camera fails or not running in Expo
 */
export async function takePhotoNative(): Promise<PhotoResult | null> {
    if (!isRunningInExpoWebView()) {
        console.warn('>>> takePhotoNative: Not running in Expo WebView')
        return null
    }

    console.log('>>> takePhotoNative: Sending TakePhoto request to native')

    try {
        const message = new Message('', MsgSource.Web, MsgTypes.TakePhoto)

        const reply = await sendAndAwaitReply(mobileEventBus$, message, {
            timeout: 60000, // 60 second timeout for camera interaction
            retries: 0, // No retries - user interaction
        })

        // Parse the reply content (it's JSON stringified in responsePayload or content)
        const resultStr = reply.content || reply.responsePayload
        const result: MediaResultResponse = JSON.parse(resultStr)

        if (result.error) {
            console.error('>>> takePhotoNative: Native camera error:', result.error)
            throw new Error(result.error)
        }

        if (result.cancelled) {
            console.log('>>> takePhotoNative: User cancelled')
            return null
        }

        console.log('>>> takePhotoNative: Photo received, size:', result.base64?.length)
        return {
            uri: result.uri,
            base64: result.base64,
            width: result.width,
            height: result.height,
            type: result.type,
            fileName: result.fileName,
        }
    } catch (error) {
        console.error('>>> takePhotoNative: Failed:', error)
        throw error
    }
}

/**
 * Record a video using the native camera
 *
 * @returns VideoResult with base64 data, or null if cancelled
 * @throws Error if camera fails or not running in Expo
 */
export async function takeVideoNative(): Promise<VideoResult | null> {
    if (!isRunningInExpoWebView()) {
        console.warn('>>> takeVideoNative: Not running in Expo WebView')
        return null
    }

    console.log('>>> takeVideoNative: Sending TakeVideo request to native')

    try {
        const message = new Message('', MsgSource.Web, MsgTypes.TakeVideo)

        const reply = await sendAndAwaitReply(mobileEventBus$, message, {
            timeout: 120000, // 2 minute timeout for video recording
            retries: 0, // No retries - user interaction
        })

        // Parse the reply content (it's JSON stringified in responsePayload or content)
        const resultStr = reply.content || reply.responsePayload
        const result: MediaResultResponse = JSON.parse(resultStr)

        if (result.error) {
            console.error('>>> takeVideoNative: Native camera error:', result.error)
            throw new Error(result.error)
        }

        if (result.cancelled) {
            console.log('>>> takeVideoNative: User cancelled')
            return null
        }

        console.log('>>> takeVideoNative: Video received, size:', result.base64?.length)
        return {
            uri: result.uri,
            base64: result.base64,
            width: result.width,
            height: result.height,
            duration: result.duration || 0,
            type: result.type,
            fileName: result.fileName,
        }
    } catch (error) {
        console.error('>>> takeVideoNative: Failed:', error)
        throw error
    }
}

/**
 * Convert a PhotoResult to a File object for upload
 */
export function photoResultToFile(result: PhotoResult): File {
    const byteCharacters = atob(result.base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: result.type })

    return new File([blob], result.fileName, { type: result.type })
}

/**
 * Convert a VideoResult to a File object for upload
 */
export function videoResultToFile(result: VideoResult): File {
    const byteCharacters = atob(result.base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: result.type })

    return new File([blob], result.fileName, { type: result.type })
}
